/**
 * Spare-parts SKU search in the collection banner. Spare-part products are
 * tagged SpareFor:<sku>, so a search is a navigation to Shopify's tag URL for
 * that tag (/collections/spare-parts/SpareFor:47286) — Shopify then does the
 * filtering. The tag prefix is owned by the Liquid (data-tag-prefix) since the
 * banner also needs it to pre-fill the field.
 *
 * Shopify 301s a tag URL with no matching products to the bare collection,
 * which would read as "every part fits", so the tag URL is probed first via
 * the Section Rendering API: a followed redirect (or non-OK response) means no
 * matches and a message is shown instead. A failed probe falls back to
 * navigating, so search still works when the check itself breaks.
 */
const SKU_TOKEN = '[sku]';

const buildSearchUrl = (collectionUrl, tagPrefix, sku) => (
  `${collectionUrl}/${tagPrefix}${encodeURIComponent(sku)}`
);

const hasTagMatches = async (searchUrl, sectionId) => {
  const url = new URL(searchUrl, window.location.origin);
  url.searchParams.set('section_id', sectionId);
  const response = await fetch(url);
  return response.ok && !response.redirected;
};

const setBusy = (formEl, submitEl, isBusy) => {
  formEl.setAttribute('aria-busy', String(isBusy));
  submitEl.disabled = isBusy;
};

const showNoResults = (messageEl, template, sku) => {
  // textContent, and a replacer function: the SKU is user input, so it must
  // not be parsed for markup or for $-patterns in a replacement string
  messageEl.textContent = template.replace(SKU_TOKEN, () => sku);
};

const initSkuSearch = () => {
  const formEl = document.querySelector('[data-sku-search-form]');
  if (!formEl || formEl.dataset.skuSearchInit) return;
  formEl.dataset.skuSearchInit = 'true';

  const inputEl = formEl.querySelector('[data-sku-search-input]');
  const submitEl = formEl.querySelector('[data-sku-search-submit]');
  const wrapperEl = formEl.closest('[data-sku-search]');
  const messageEl = wrapperEl?.querySelector('[data-sku-search-message]');
  const { collectionUrl, tagPrefix, sectionId, noResultsText } = formEl.dataset;
  if (!inputEl || !submitEl || !messageEl) return;
  if (!collectionUrl || !tagPrefix || !sectionId || !noResultsText) return;

  // A back-forward cache restore brings the page back mid-navigation, with
  // the button still disabled from the probe that succeeded
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) setBusy(formEl, submitEl, false);
  });

  const searchSku = async (sku) => {
    const searchUrl = buildSearchUrl(collectionUrl, tagPrefix, sku);
    setBusy(formEl, submitEl, true);
    try {
      const hasMatches = await hasTagMatches(searchUrl, sectionId);
      if (hasMatches) {
        window.location.assign(searchUrl);
        return;
      }
      showNoResults(messageEl, noResultsText, sku);
      setBusy(formEl, submitEl, false);
    } catch (error) {
      console.error(error);
      window.location.assign(searchUrl);
    }
  };

  formEl.addEventListener('submit', (event) => {
    event.preventDefault();
    if (submitEl.disabled) return;
    messageEl.textContent = '';
    const sku = inputEl.value.trim();
    if (!sku) {
      window.location.assign(collectionUrl);
      return;
    }
    searchSku(sku);
  });
};

export default initSkuSearch;
